import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import { CalendarIcon, X } from 'lucide-react';
import { DateRange } from 'react-day-picker';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { track } from '@/lib/mixpanel';
import { cn } from '@/lib/utils';

interface DateRangePickerProps {
  dateRange?: DateRange;
  onChange?: (range: DateRange | undefined) => void;
  onClear?: () => void;
  placeholder?: string;
}

export const DateRangePicker = ({
  dateRange,
  onChange,
  onClear,
  placeholder = 'Interval de date',
}: DateRangePickerProps) => {
  const displayValue = dateRange?.from
    ? dateRange.to
      ? `${format(dateRange.from, 'LLL dd, y', { locale: ro })} - ${format(dateRange.to, 'LLL dd, y', { locale: ro })}`
      : format(dateRange.from, 'LLL dd, y', { locale: ro })
    : undefined;

  const showClearButton = Boolean(dateRange && onClear);

  const handleDateChange = (range: DateRange | undefined) => {
    if (onChange) {
      onChange(range);
      track('Homepage_DateRangeChanged', {
        hasFrom: Boolean(range?.from),
        hasTo: Boolean(range?.to),
      });
    }
  };

  const handleClear = () => {
    if (onClear) {
      onClear();
      track('Homepage_DateRangeCleared');
    }
  };

  return (
    <div className="relative">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'flex h-9 items-center justify-start border-neutral-300 bg-white text-left font-normal text-neutral-700 hover:bg-neutral-100',
              showClearButton && 'pr-10',
              !displayValue && 'text-muted-foreground'
            )}>
            <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
            <span className="min-w-0 truncate text-left">
              {displayValue ?? placeholder}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            autoFocus
            mode="range"
            defaultMonth={dateRange?.from}
            selected={dateRange}
            onSelect={handleDateChange}
            numberOfMonths={2}
            locale={ro}
          />
        </PopoverContent>
      </Popover>

      {showClearButton && (
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClear}
          className="absolute right-1 top-1 h-7 w-7 p-0">
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
};
